// Identity / member query layer. Pairs with `lib/views/identity.ts` (server
// fetchers used during SSR prefetch) and `lib/mutations/identity.ts`.
// No 'use client' directive: the hooks are React hooks but they're called
// from client components. Server code (pages, layouts) imports the query
// leaves to set up prefetches.
//
// Per the react-query-options-migration design: every read is a `queryOptions`
// / `infiniteQueryOptions` factory whose cache key and fetch input derive from
// one normalized `args` object built adjacent to the `queryFn`. Inner-node
// prefixes (`all`, `members`) stay bare arrays for invalidation; leaves return
// `queryOptions`. Normalization happens once, in the leaf.

import {
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { asUserId, asSessionId } from '@leanin/shared/ids';
import { resolveLocale, type UserLocale } from '@leanin/shared';
import type { UserId } from '@leanin/shared/ids';
import type { AuthedSession } from '@leanin/shared/auth/session';
import type { AuthDriftReason } from '@leanin/shared/auth/drift';
import { SELF_REACTIVATE_WINDOW_MS } from '@leanin/shared/auth/windows';
import { PRESENCE_LOOKUP_MAX_IDS, PRESENCE_POLL_INTERVAL_MS } from '@leanin/shared/presence';
import { MEMBER_PROFILES_BATCH_MAX_IDS, type PlatformRole } from '@leanin/shared/views/profile';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { reportRequestFailure, type ReportError } from '../report-error';
import { throwApiError, unwrap } from '../api-error';
import { sortedStrings } from './normalize';

export type MyProfileView = components['schemas']['ProfileSelfResponse'];
export type MemberView = components['schemas']['ProfileView'];
export type ProfileView = components['schemas']['ProfileView'];
export type UserMutualsView = components['schemas']['MemberMutuals'];
export type MyPrivacyView = components['schemas']['UserPrivacyView'];
export type MembersCountByScopeView =
  components['schemas']['DirectoryScopeCounts'];

export type ListMembersInput = {
  q?: string;
  sort?: 'recent' | 'name' | 'connection' | 'recommended';
  cursor?: string;
  pageSize?: number;
  placeCountryCode?: string;
  networkId?: string;
  scope?: 'circle' | 'network';
  /** Restrict to people who can see content scoped to this group (members or
   *  child-circle members) — used for scoped @-mention autocomplete. */
  audienceGroupId?: string;
  /** Multi-value filter for the unified place_text column. The server accepts
   *  an array so callers can OR-filter across several cities. */
  placeText?: readonly string[];
  /** Multi-value US-state filter (USPS codes) — word-precise server-side. */
  placeStates?: readonly string[];
  networks?: readonly string[];
  industries?: readonly string[];
  careerStages?: readonly string[];
  openTo?: readonly string[];
};

// The normalized member-list args: array filters sorted so the same set in a
// different order produces the same cache key (closes order-dependent drift).
// `cursor` is fetch-only — injected via `pageParam` for the infinite leaf and
// never part of the key.
type NormalizedListMembersArgs = {
  q?: string;
  sort?: ListMembersInput['sort'];
  pageSize?: number;
  placeCountryCode?: string;
  networkId?: string;
  scope?: ListMembersInput['scope'];
  audienceGroupId?: string;
  placeText?: readonly string[];
  placeStates?: readonly string[];
  networks?: readonly string[];
  industries?: readonly string[];
  careerStages?: readonly string[];
  openTo?: readonly string[];
};

function normalizeListMembers(
  input: Omit<ListMembersInput, 'cursor'>,
): NormalizedListMembersArgs {
  const args: NormalizedListMembersArgs = {};
  if (input.q !== undefined) args.q = input.q;
  if (input.sort !== undefined) args.sort = input.sort;
  if (input.pageSize !== undefined) args.pageSize = input.pageSize;
  if (input.placeCountryCode !== undefined)
    args.placeCountryCode = input.placeCountryCode;
  if (input.networkId !== undefined) args.networkId = input.networkId;
  if (input.scope !== undefined) args.scope = input.scope;
  if (input.audienceGroupId !== undefined)
    args.audienceGroupId = input.audienceGroupId;
  if (input.placeText !== undefined) args.placeText = sortedStrings(input.placeText);
  if (input.placeStates !== undefined)
    args.placeStates = sortedStrings(input.placeStates);
  if (input.networks !== undefined) args.networks = sortedStrings(input.networks);
  if (input.industries !== undefined)
    args.industries = sortedStrings(input.industries);
  if (input.careerStages !== undefined)
    args.careerStages = sortedStrings(input.careerStages);
  if (input.openTo !== undefined) args.openTo = sortedStrings(input.openTo);
  return args;
}

// ---- Query keys + leaves ----

export const identityKeys = {
  all: ['identity'] as const,
  members: () => [...identityKeys.all, 'members'] as const,

  me: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'me'] as const,
      queryFn: fetchMyProfile,
      // me changes rarely; keep it fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    }),

  member: (id: UserId) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'member', id] as const,
      queryFn: () => fetchMember(id),
    }),

  // Resolver entry for a `/members/{ref}` arrival, where `ref` is a handle
  // or a uuid. Deliberately the same key shape as `member`, so a uuid
  // arrival collapses onto the uuid entry and fetches once. A handle
  // arrival gets its own entry, which exists only to yield the uuid — the
  // page's display query stays uuid-keyed, because every mutation-side
  // cache patch and invalidation targets `member(uuid)`.
  memberRef: (ref: string) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'member', ref] as const,
      queryFn: () => fetchMember(ref),
    }),

  memberMutuals: (id: UserId) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'member', id, 'mutuals'] as const,
      queryFn: () => fetchMemberMutuals(id),
    }),

  // Single-page member list (e.g. mention popover). Normalize once; `args` is
  // the terminal key element and the sole fetch input. `keepPreviousData`
  // (parity with membersInfinite) keeps the prior suggestions on screen while
  // the next prefix loads, so the typeahead never blanks to a spinner
  // mid-keystroke.
  membersList: (raw: ListMembersInput) => {
    const args = normalizeListMembers(raw);
    return queryOptions({
      queryKey: [...identityKeys.members(), args] as const,
      queryFn: () => fetchMembers(args),
      placeholderData: keepPreviousData,
    });
  },

  // Cursor-paginated member list (directory). Same normalized `args` in the
  // key; cursor injected via `pageParam`, never in the key.
  membersInfinite: (raw: Omit<ListMembersInput, 'cursor'>) => {
    const args = normalizeListMembers(raw);
    return infiniteQueryOptions({
      queryKey: [...identityKeys.members(), args] as const,
      queryFn: ({ pageParam }) =>
        fetchMembers({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        (lastPage as { nextCursor?: string | null }).nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  },

  membersCountByScope: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'members', 'count-by-scope'] as const,
      queryFn: fetchMembersCountByScope,
    }),

  // Directory location typeahead: alias-aware (US states / countries)
  // typed suggestions plus matching member locations, keyed by the typed
  // query. Cached briefly per query — the vocabulary shifts only as
  // members join or edit their location.
  memberFacets: (q?: string) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'members', 'facets', q ?? ''] as const,
      queryFn: () => fetchMemberFacets(q),
      staleTime: 5 * 60 * 1000,
    }),

  // Read through `useOnlineMembers` (lib/presence-subscriptions), which keys
  // this on the union of every id the tab currently shows.
  membersOnline: (ids: readonly UserId[]) => {
    const sortedIds = [...new Set(ids)].sort();
    return queryOptions({
      queryKey: [...identityKeys.all, 'members', 'online', sortedIds] as const,
      queryFn: () => fetchOnlineMembers(sortedIds),
      enabled: sortedIds.length > 0,
      staleTime: 30 * 1000,
      // Re-read on the presence poll cadence so an open inbox or dock notices
      // a peer coming online (or going quiet) without a remount. The interval
      // pauses while the tab is hidden, so returning to the tab re-reads
      // (once stale) instead of showing dots up to a poll interval old.
      refetchInterval: PRESENCE_POLL_INTERVAL_MS,
      refetchOnWindowFocus: true,
      // A subscriber joining or leaving changes the key; keep the dots already
      // on screen while the wider read is in flight instead of blanking them.
      placeholderData: keepPreviousData,
    });
  },

  memberProfiles: (ids: readonly UserId[]) => {
    const sortedIds = [...ids].sort();
    return queryOptions({
      queryKey: [...identityKeys.all, 'members', 'profiles', sortedIds] as const,
      queryFn: () => fetchMemberProfiles(sortedIds),
      enabled: ids.length > 0,
    });
  },

  follow: (id: UserId) =>
    queryOptions({
      queryKey: [...identityKeys.all, 'follow', id] as const,
      queryFn: () => fetchIsFollowing(id),
    }),

  privacy: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'privacy'] as const,
      queryFn: fetchMyPrivacy,
    }),

  consentHistory: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'consent-history'] as const,
      queryFn: fetchMyConsentHistory,
    }),

  // Cursor-paginated consent history (Settings → Privacy → Consent history).
  // Cursor injected via `pageParam`, never in the key. Mirrors
  // `membersInfinite` — keyed bare so the page list invalidates cleanly.
  consentHistoryInfinite: () =>
    infiniteQueryOptions({
      queryKey: [...identityKeys.all, 'consent-history', 'infinite'] as const,
      queryFn: ({ pageParam }) =>
        fetchMyConsentHistoryPage({ cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  notificationPreferences: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'notifications', 'preferences'] as const,
      queryFn: fetchMyNotificationPreferences,
    }),

  sessions: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'sessions'] as const,
      queryFn: fetchMySessions,
    }),

  connectedAccounts: () =>
    queryOptions({
      queryKey: [...identityKeys.all, 'connected-accounts'] as const,
      queryFn: fetchMyConnectedAccounts,
    }),
};

// ---- Client fetchers ----

export async function fetchMyProfile(): Promise<MyProfileView> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/me'));
  return data.data;
}

// `ref` is the member's uuid or their handle — `GET /members/{id}` accepts
// either and resolves a handle server-side before any SQL.
export async function fetchMember(ref: string): Promise<MemberView> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/{id}', {
    params: { path: { id: ref } },
  }));
  return data.data;
}

export async function fetchIsFollowing(id: UserId): Promise<boolean> {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/me/follows/{userId}', {
    params: { path: { userId: id } },
  });
  return data?.following ?? false;
}

export async function fetchMemberMutuals(
  id: UserId,
): Promise<UserMutualsView> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/members/{userId}/mutuals',
    { params: { path: { userId: id } } },
  ));
  return data;
}

export async function fetchMembers(input: NormalizedListMembersArgs & { cursor?: string }) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members', {
    params: {
      query: {
        q: input.q,
        sort: input.sort,
        cursor: input.cursor,
        pageSize: input.pageSize,
        placeCountryCode: input.placeCountryCode,
        networkId: input.networkId,
        scope: input.scope,
        audienceGroupId: input.audienceGroupId,
        ...(input.placeText !== undefined
          ? { placeText: input.placeText as string[] }
          : {}),
        ...(input.placeStates !== undefined
          ? { placeStates: input.placeStates as string[] }
          : {}),
        ...(input.networks !== undefined
          ? { networks: input.networks as unknown as string[] }
          : {}),
        ...(input.industries !== undefined
          ? { industries: input.industries as string[] }
          : {}),
        ...(input.careerStages !== undefined
          ? { careerStages: input.careerStages as string[] }
          : {}),
        ...(input.openTo !== undefined
          ? { openTo: input.openTo as string[] }
          : {}),
      },
    },
  }));
  return data;
}

export async function fetchMemberFacets(q?: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/facets', {
    params: {
      query: {
        ...(q !== undefined && q.length > 0 ? { q } : {}),
      },
    },
  }));
  return data;
}

export async function fetchOnlineMembers(
  ids: readonly UserId[],
): Promise<Set<UserId>> {
  if (ids.length === 0) return new Set();
  const client = getApiClient();
  const chunks: UserId[][] = [];
  for (let i = 0; i < ids.length; i += PRESENCE_LOOKUP_MAX_IDS) {
    chunks.push(ids.slice(i, i + PRESENCE_LOOKUP_MAX_IDS));
  }
  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const data = unwrap(await client.GET('/api/v1/members/online', {
        params: { query: { userIds: chunk } },
      }));
      return data.onlineUserIds as UserId[];
    }),
  );
  return new Set(pages.flat());
}

export async function fetchMyPrivacy(): Promise<MyPrivacyView> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/me/privacy'));
  return data.data;
}

export async function fetchMyConsentHistory() {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/members/me/consent-history',
  ));
  return data;
}

export async function fetchMyConsentHistoryPage(input: {
  cursor?: string;
  pageSize?: number;
}) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/members/me/consent-history',
    { params: { query: { cursor: input.cursor, pageSize: input.pageSize } } },
  ));
  return data;
}

export async function fetchMyNotificationPreferences() {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/members/me/notification-preferences',
  ));
  return data.data;
}

export async function fetchMySessions() {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/me/sessions'));
  return data;
}

export async function fetchMyConnectedAccounts() {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/members/me/connected-accounts',
  ));
  return data;
}

export async function fetchMembersCountByScope(): Promise<MembersCountByScopeView> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/members/count-by-scope',
  ));
  return data;
}

export async function fetchMemberProfiles(
  ids: readonly UserId[],
): Promise<Map<UserId, ProfileView>> {
  if (ids.length === 0) return new Map();
  const client = getApiClient();
  const chunks: UserId[][] = [];
  for (let i = 0; i < ids.length; i += MEMBER_PROFILES_BATCH_MAX_IDS) {
    chunks.push(ids.slice(i, i + MEMBER_PROFILES_BATCH_MAX_IDS));
  }
  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const data = unwrap(await client.POST('/api/v1/members/profiles', {
        body: { userIds: chunk },
      }));
      return data.profiles;
    }),
  );
  return new Map(pages.flat().map((p) => [p.id as UserId, p]));
}

// ---- Session query ----
//
// The canonical session leaf. One cache key (`['me', 'context']`) — every
// consumer (router guards, layouts, component hooks) routes through the same
// TanStack subscription, so a single network call backs the session window.
// Co-located here per the migration design so route loaders (which bypass
// hooks) and `use-session.ts`'s hooks share one definition.

export type Member = {
  id: string;
  firstName: string;
  lastName: string;
  bio: string | null;
  photoStorageKey: string | null;
  photoStatus: string;
  photoVariants: readonly string[] | null;
  platformRole: PlatformRole | null;
  locale: UserLocale;
  emailVerifiedAt: Date | null;
  onboardingCompletedAt: Date | null;
  restricted: boolean;
  deactivatedAt: Date | null;
  createdAt: Date;
};

// Server-evaluated capability bits for the viewer (GET /me/context). Each is
// a composed rule predicate — the client never derives them from role fields.
export type MeCapabilities = {
  /** Network leader of any active Network, or platform admin. */
  canViewRoadmap: boolean;
  /** Leader of any active Network (admins excluded). */
  leadsNetwork: boolean;
};

// Binary context. The server's auth middleware Just-In-Time provisions the
// local member row on first authed request for a new Clerk user (see
// `apps/server/src/identity/service/load-session.ts`), so the SPA never sees
// an "orphan" state — either we have an authed session or we don't.
export type MyContext = {
  session: AuthedSession;
  member: Member;
  capabilities: MeCapabilities;
} | null;

// Client-side mirror of the server's drift-final classification in
// `loadSessionFromClerk`: a restricted or past-window-deactivated row can
// never back a usable session. Returns the same discriminator the server
// puts on its 401 (`authState`) so both detection paths feed one recovery
// flow. Within-window deactivation is NOT drift-final — those members get
// a session and the reactivation prompt.
function driftReasonFor(m: Member): AuthDriftReason | null {
  if (m.restricted) return 'restricted';
  if (m.deactivatedAt !== null) {
    const elapsed = Date.now() - m.deactivatedAt.getTime();
    if (elapsed > SELF_REACTIVATE_WINDOW_MS) return 'deactivated_expired';
  }
  return null;
}

// Why the session-fetch drift verdict is carried in module state rather than
// the query cache: `fetchMyContext` must still resolve `null` (the routing
// guards' signed-out contract), but the redirect that follows wants to tell
// the sign-in page WHY. The reason waits in module memory, never persisted,
// until whatever sends the visitor to /sign-in consumes it with
// `takeAuthDrift()` (a route guard, the `/` route, the login form,
// /auth/finishing) and passes it along as a search param. `/` may consume one
// an earlier navigation's probe set, which is how a drift found on a public
// page still reaches the banner.
let pendingAuthDrift: AuthDriftReason | null = null;

export function takeAuthDrift(): AuthDriftReason | null {
  const reason = pendingAuthDrift;
  pendingAuthDrift = null;
  return reason;
}

// Clerk attaches itself to `window.Clerk` once the SDK initialises (see
// ClerkProvider in `app.tsx`). Typed loosely here: we only need `signOut`
// + a session-presence probe, and importing the SDK type pulls in heavy types
// we don't otherwise need at the api-client layer.
type ClerkLike = {
  readonly session?: unknown;
  signOut: (options?: { readonly redirectUrl?: string }) => Promise<void>;
};

function parseAuthState(error: unknown): AuthDriftReason | null {
  const inner = (error as { error?: { authState?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  const v = inner.authState;
  return v === 'restricted' || v === 'deactivated_expired' ? v : null;
}

// Reconcile when the SDK and the server disagree on a drift-final state.
// Drift-final = the server positively identified that the local member row is
// non-recoverable (restricted or hard-deactivated past the self-reactivate
// window) AND the caller had a valid Clerk JWT. Dropping the SDK session here
// is the right move because the Clerk session can no longer back a usable app
// session. For every OTHER 401 (transient backend failure, Clerk outage,
// plain anonymous, unknown) we leave the SDK alone — server-side flakiness
// should not destructively sign the user out on first occurrence.
function reconcileStaleClerkSession(reason: AuthDriftReason): void {
  if (typeof window === 'undefined') return;
  // Dev-bypass cookie clear happens before the Clerk probe: in bypass mode
  // there is no Clerk session, and gating the clear behind one left the
  // cookie alive — every subsequent request re-presented the dead identity.
  document.cookie = 'dev_user_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  const clerk = (window as unknown as { Clerk?: ClerkLike }).Clerk;
  if (!clerk?.session) return;
  // `redirectUrl` is load-bearing, not a convenience: Clerk.signOut()
  // performs its own post-teardown navigation (afterSignOutUrl, default
  // `/`) that fires AFTER whatever redirect the SPA has already made,
  // stomping it and wiping the in-memory drift reason — the observed
  // failure was Google sign-ins to a dead account landing wordlessly on
  // /auth/signup. Naming the destination here makes Clerk's own
  // navigation deliver the user to the reason banner.
  void clerk.signOut({ redirectUrl: `/sign-in?reason=${reason}` });
}

async function fetchMyContext(): Promise<MyContext> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/me/context');
  if (!data) {
    if (response.status === 401) {
      // Per Option B of the recovery-design: discriminate on the
      // server-emitted `authState`. Reconcile (destructively drop the Clerk
      // SDK session) ONLY when the server has positively identified a
      // drift-final cause. Bare UNAUTHENTICATED — the transient case —
      // returns null without reconciling, so the next poll picks the session
      // back up cleanly when the upstream recovers. (Today's failure mode: a
      // single Clerk Backend API outage or DB blip otherwise destructively
      // logged every signed-in user out across the deployment.)
      const authState = parseAuthState(error);
      if (authState !== null) {
        pendingAuthDrift = authState;
        reconcileStaleClerkSession(authState);
      }
      return null;
    }
    if (response.status === 404) return null;
    throwApiError(error, response);
  }
  const raw = data.member;
  const member: Member = {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    bio: raw.bio ?? null,
    photoStorageKey: raw.photoStorageKey ?? null,
    photoStatus: raw.photoStatus,
    // Absent while an ECS rolling deploy still routes some requests to tasks
    // that predate the field; `null` keeps the legacy mint instead of crashing.
    photoVariants: raw.photoVariants ?? null,
    platformRole: raw.platformRole ?? null,
    locale: resolveLocale(raw.locale),
    emailVerifiedAt: raw.emailVerifiedAt != null ? new Date(raw.emailVerifiedAt) : null,
    onboardingCompletedAt:
      raw.onboardingCompletedAt != null ? new Date(raw.onboardingCompletedAt) : null,
    restricted: raw.restricted,
    deactivatedAt: raw.deactivatedAt != null ? new Date(raw.deactivatedAt) : null,
    createdAt: new Date(raw.createdAt),
  };
  // Drift-final state detected on a 200 — the row flipped between the JWT
  // mint and this read, so there's no `authState` to parse. Same recovery as
  // the 401 path: reconcile the SDK session and surface the reason.
  const drift = driftReasonFor(member);
  if (drift !== null) {
    pendingAuthDrift = drift;
    reconcileStaleClerkSession(drift);
    return null;
  }
  const s = data.session;
  const session: AuthedSession = {
    sessionId: asSessionId(s.sessionId),
    userId: asUserId(s.userId),
    platformRole: s.platformRole ?? null,
    emailVerified: s.emailVerified,
    stepUpValidUntil: s.stepUpValidUntil != null ? new Date(s.stepUpValidUntil) : null,
    absoluteExpiresAt: new Date(s.absoluteExpiresAt),
    idleExpiresAt: new Date(s.idleExpiresAt),
  };
  return {
    session,
    member,
    capabilities: {
      canViewRoadmap: data.capabilities.canViewRoadmap,
      leadsNetwork: data.capabilities.leadsNetwork,
    },
  };
}

export const sessionQuery = queryOptions({
  queryKey: ['me', 'context'] as const,
  queryFn: fetchMyContext,
  staleTime: 60_000,
  // A failed probe is not sent again just because another component starts
  // observing it: the locale provider, the route guard and the layout each
  // observe this one query, so react-query's default re-sent every failure
  // as the layout mounted. A failure is retried on purpose instead: by a
  // guard's next `ensureQueryData` (each navigation, and the session error
  // page's retry), on reconnect, and by the refetch a sign-in runs.
  retryOnMount: false,
});

// Inverse of the `_authed` guard: a visitor who already has a usable session
// is sent on to `next` (the page a sign-in bounce was carrying, already
// validated as an in-app path) or /feed. The Join landing (`/sign-in`,
// `/get-started`, `/`), `/auth/signup` and `/auth/password-reset` make no
// sense for signed-in users (and Clerk rejects `signIn.create()` calls while
// signed in).
//
// For a route under `_public`, which has probed the session already: its
// verdict comes from the layout's context, so a failed probe is not sent
// (or reported) a second time.
export function redirectSignedInVisitor(session: AuthedSession | null, next?: string): void {
  if (session === null) return;
  if (next !== undefined) throw redirect({ href: next });
  throw redirect({ to: '/feed' });
}

// The same, for a route outside `_public`, which probes the session itself.
// A THROWING probe (network failure, 5xx) fails open: the visitor sees the
// sign-in / sign-up form, which is the right surface when we can't tell
// whether they're signed in. `/` is the exception: the `_public` layout
// fails it closed before this runs. The failure is reported
// (`reportRequestFailure`), not shown. redirect()
// returns a `Response`, so the fetch is isolated and the redirect thrown
// outside the try.
export async function redirectIfSignedIn(
  context: { queryClient: QueryClient; reportError: ReportError },
  next?: string,
): Promise<void> {
  let ctx: MyContext;
  try {
    ctx = await context.queryClient.ensureQueryData(sessionQuery);
  } catch (err) {
    reportRequestFailure(context.reportError, err, {
      flow: 'auth.session_probe',
      guard: 'redirect_if_signed_in',
    });
    return;
  }
  redirectSignedInVisitor(ctx === null ? null : ctx.session, next);
}

// Onboarding gate. A signed-in visitor whose `onboardingCompletedAt` is null
// hasn't finished the persona tour — they get bounced to `/onboarding/profile`.
// Anonymous visitors are passed through (the gate only fires when `ctx` is
// non-null).
export function gateOnboardingComplete(ctx: MyContext): void {
  if (ctx === null) return;
  // Deactivated-within-window members skip the onboarding bounce: the
  // authed/public shells render the reactivation prompt instead of the app,
  // and routing them into onboarding first would strand them in a flow
  // where every write 403s `identity/deactivated`. The gate re-fires on the
  // first navigation after they reactivate.
  if (ctx.member.deactivatedAt !== null) return;
  if (ctx.member.onboardingCompletedAt === null) {
    throw redirect({ to: '/onboarding/profile' });
  }
}

// Inverse of `gateOnboardingComplete`, applied by the `/onboarding` layout: a
// signed-in visitor who has ALREADY finished onboarding has no business on the
// `/onboarding/*` flow — bounce them to /feed.
export function gateOnboardingIncomplete(ctx: MyContext): void {
  if (ctx === null) return;
  if (ctx.member.onboardingCompletedAt !== null) {
    throw redirect({ to: '/feed' });
  }
}

// ---- Hooks ----

export function useMe() {
  return useQuery(identityKeys.me());
}

export function useMember(id: UserId) {
  return useQuery(identityKeys.member(id));
}

export function useIsFollowing(id: UserId) {
  return useQuery(identityKeys.follow(id));
}

export function useMemberMutuals(id: UserId) {
  return useQuery(identityKeys.memberMutuals(id));
}

export function useMembers(input: ListMembersInput) {
  return useQuery(identityKeys.membersList(input));
}

export function useInfiniteMembers(input: Omit<ListMembersInput, 'cursor'>) {
  return useInfiniteQuery(identityKeys.membersInfinite(input));
}

export function useMemberProfiles(ids: readonly UserId[]) {
  return useQuery(identityKeys.memberProfiles(ids));
}

export function useMyPrivacy() {
  return useQuery(identityKeys.privacy());
}

export function useInfiniteConsentHistory() {
  return useInfiniteQuery(identityKeys.consentHistoryInfinite());
}

export function useMembersCountByScope() {
  return useQuery(identityKeys.membersCountByScope());
}

export function useMemberFacets(q?: string) {
  return useQuery(identityKeys.memberFacets(q));
}
